import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { X, MapPin, ExternalLink, Info, AlertTriangle, Loader2 } from 'lucide-react';
import Sphere360Viewer from '../Sphere360Viewer';
import { sw, panoFileUrl, coordToView } from '../../lib/sitewalkApi';

/**
 * Reusable EXACT-evidence viewer. Resolves Project > SiteWalk > Capture Location > Capture Point >
 * Panorama > coord(yaw/pitch) > Hotspot from any starting id and restores the panorama oriented to
 * the hotspot. Opening evidence NEVER mutates the source record (governance).
 */
export default function Evidence360Modal({ projectId, params, onClose }) {
  const navigate = useNavigate();
  const [chain, setChain] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    sw.resolve(projectId, params)
      .then((d) => { if (alive) { setChain(d); setLoading(false); } })
      .catch((e) => { if (alive) { setErr(e.response?.data?.detail || 'Evidence not accessible'); setLoading(false); } });
    return () => { alive = false; };
  }, [projectId, JSON.stringify(params)]);

  const pano = chain?.panorama;
  const ready = pano && pano.processingStatus === 'READY';
  const hs = chain?.hotspot;
  const view = coordToView(chain?.coord, 55);

  const openFullWalk = () => {
    const wid = chain?.siteWalk?.id;
    if (!wid) return toast.error('Full walk not accessible');
    const q = new URLSearchParams();
    if (chain?.capturePoint?.id) q.set('cp', chain.capturePoint.id);
    if (hs?.id) q.set('hotspot', hs.id);
    navigate(`/app/projects/${projectId}/site-walks/${wid}?${q.toString()}`);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-2 md:p-6" data-testid="evidence-360-modal">
      <div className="bg-white rounded-xl w-full max-w-4xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between px-4 py-3 border-b sticky top-0 bg-white z-10">
          <h3 className="font-bold text-slate-800 flex items-center gap-2"><MapPin className="w-5 h-5 text-sky-600" /> 360° Evidence</h3>
          <button onClick={onClose} data-testid="evidence-360-close"><X className="w-5 h-5 text-slate-400" /></button>
        </div>
        <div className="p-4">
          {loading ? (
            <div className="h-80 flex items-center justify-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
          ) : err ? (
            <div className="h-40 flex flex-col items-center justify-center text-center gap-2" data-testid="evidence-360-error">
              <AlertTriangle className="w-8 h-8 text-amber-500" />
              <p className="text-sm text-slate-600">{err}</p>
            </div>
          ) : (
            <>
              {/* Chain breadcrumb */}
              <div className="flex flex-wrap items-center gap-1.5 text-[11px] text-slate-500 mb-2" data-testid="evidence-chain">
                {chain?.siteWalk && <span className="px-1.5 py-0.5 bg-slate-100 rounded">{chain.siteWalk.walkNumber}</span>}
                {chain?.captureLocation?.name && <><span>›</span><span className="px-1.5 py-0.5 bg-slate-100 rounded">{chain.captureLocation.name}</span></>}
                {chain?.capturePoint?.name && <><span>›</span><span className="px-1.5 py-0.5 bg-slate-100 rounded">{chain.capturePoint.name}</span></>}
                {chain?.capturePoint?.capturedAt && <><span>›</span><span>{chain.capturePoint.capturedAt.slice(0, 10)}</span></>}
                {hs && <><span>›</span><span className="px-1.5 py-0.5 bg-sky-100 text-sky-700 rounded">hotspot {hs.type}</span></>}
              </div>

              {ready ? (
                <Sphere360Viewer imageUrl={panoFileUrl(projectId, pano.id)}
                  hotspots={hs ? [hs] : []} viewOverride={view ? { ...view, nonce: Date.now() } : undefined} />
              ) : (
                <div className="h-72 rounded-lg bg-slate-100 flex flex-col items-center justify-center gap-2" data-testid="evidence-360-unavailable">
                  <AlertTriangle className="w-7 h-7 text-amber-500" />
                  <p className="text-sm text-slate-600">{pano ? `Panorama ${pano.processingStatus?.toLowerCase() || 'unavailable'} — original evidence retained` : 'No panorama linked to this evidence yet'}</p>
                </div>
              )}

              <div className="flex flex-wrap items-center justify-between gap-2 mt-3">
                <p className="text-[11px] text-slate-400 flex items-center gap-1" data-testid="evidence-governance-note">
                  <Info className="w-3.5 h-3.5" /> Orientation restored from stored yaw/pitch (viewer orientation only, not geometric registration).
                </p>
                <button onClick={openFullWalk} className="text-xs flex items-center gap-1 px-3 py-1.5 rounded-lg bg-sky-600 text-white" data-testid="evidence-open-walk">
                  <ExternalLink className="w-3.5 h-3.5" /> Open in Site Walk
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
